/**
 * Release runbook — the canonical ordered steps for cutting a racemarks app
 * release to the App Store + Play Store.
 *
 * Mirrors the runbook tracked in bead ra-hy9e (and per branch-policy /
 * app_release_via_release_branch): content freeze -> derived build# ->
 * release/1.3.x branch -> release.yml -> mobile artifact builds -> store
 * uploads -> the ci draft-release stage (ASC version + Play draft, build
 * attached) -> the two human SUBMIT steps -> confirm -> rev main.
 *
 * Each step carries an `actor` describing who/what advances it:
 *   - 'ci'    GitHub Actions (release.yml / Build Mobile Artifacts)
 *   - 'auto'  derived / carried automatically (build#, nightly web carry)
 *   - 'human' a person must act (Chris drives the final store submits)
 *
 * An optional `action` names a deep-link affordance rendered next to the step:
 *   - 'workflow'  the Build Mobile Artifacts run (build/upload)
 *   - 'draft'     the Create Draft Release run (draft-release.yml — ASC + Play)
 *   - 'asc'       App Store Connect submit page
 *   - 'play'      Play Console submit page
 *   - 'web'       the racemarks.app web preview URL (per-release, from the doc)
 *
 * The step definitions encode the OUTCOME and the TIMING of each stage, not the
 * exact mechanism — how a stage is deployed may change, but where it must land
 * and when it happens in the cut are fixed here. Kept in sync with
 * docs/release-runbook.md (the authoritative prose runbook).
 */

export const STATUS = {
  DONE: 'done',
  IN_PROGRESS: 'in-progress',
  PENDING: 'pending',
  FAILED: 'failed',
};

const VALID_STATUSES = new Set(Object.values(STATUS));

export const RUNBOOK = [
  { id: 'content-merged', label: 'All intended app PRs merged to main', actor: 'human', note: 'Freeze main once the cut scope is in — main stays frozen at the shipping version until the cut build uploads.' },
  { id: 'copy-published', label: 'Store copy / release notes published', actor: 'human', note: 'TestFlight "What to Test" + store "What\'s New".' },
  { id: 'nightly-complete', label: 'Web snapshot carried to release/web/X.Y', actor: 'auto', note: 'carry-web-to-release snapshots web/racemarks.app + web/committee from main onto release/web/X.Y (opens a PR — the branch is protected).' },
  { id: 'committee-prod', label: 'Committee deployed to PROD', actor: 'ci', note: 'Committee portal live on prod for this release — EARLY, before the app cut. Outcome-fixed; the mechanism (deploy-committee.yml on release/web push) may change.' },
  { id: 'web-preview', label: 'Web on preview URL', actor: 'ci', note: 'racemarks.app web build live on its per-release preview URL — EARLY, before the app cut. The URL is per-release (sourced from the release_status doc, not hardcoded).', action: 'web' },
  { id: 'build-derived', label: 'Build # derived (multiple of 10, e.g. +170)', actor: 'human', note: 'Read the latest ASC nightly build# (TestFlight), round UP to the next multiple of 10. The prod build-mobile job reads pubspec as-is — there is NO ASC-max+1 safety net, so this number must be right.' },
  { id: 'branch-cut', label: 'release/1.3.x branch ready at the cut build#', actor: 'ci', note: 'release/1.3.x is branch-protected — land the pubspec bump via PR, not a direct push. Long-lived per minor; for a point release the branch already exists.' },
  { id: 'release-triggered', label: 'Build Mobile Artifacts triggered (tag pushed from release/1.3.x)', actor: 'ci', note: 'Push the v-tag from release/1.3.x HEAD — a tag push triggers build-mobile.yml on that ref. Do NOT use release.yml/--ref main: it builds main HEAD, which is the #737 trap.', action: 'workflow' },
  { id: 'ios-build', label: 'iOS build — self-hosted runner (~45 min)', actor: 'ci', note: 'Compiles on this machine; cancels under contention — run off-peak.', action: 'workflow' },
  { id: 'android-build', label: 'Android AAB build', actor: 'ci', action: 'workflow' },
  { id: 'asc-upload', label: 'Upload to App Store Connect (IPA)', actor: 'ci', note: 'Never reuse a versionCode. altool can report a FALSE GREEN — confirm the build actually lands in ASC/TestFlight, not just CI-green.', action: 'workflow' },
  { id: 'play-upload', label: 'Upload to Play Console (AAB)', actor: 'ci', action: 'workflow' },
  { id: 'draft-release', label: 'Create draft release (ASC version + Play draft, build attached)', actor: 'ci', note: 'draft-release.yml: find-or-create the ASC App Store version + link the build, and create-or-ensure the Play production DRAFT with the build attached — both idempotent, both left as DRAFT. You cannot submit for review without a draft to attach the uploaded build to; this stages it so the two submits below are a single human click. Never submits/rolls out. Run once the uploaded build has finished processing (ASC build must be VALID).', action: 'draft' },
  { id: 'asc-submit', label: 'Submit for App Store review', actor: 'human', note: 'Chris — manual submit.', action: 'asc' },
  { id: 'play-submit', label: 'Submit / roll out on Play Console', actor: 'human', note: 'Chris — manual submit.', action: 'play' },
  { id: 'stores-confirmed', label: 'Both stores confirm upload/submit', actor: 'human', note: 'Verify at the STORE, not on CI-green. Mind the ~12-min ASC processing window — a just-uploaded build reads as missing (false negative) until it appears.' },
  { id: 'rev-main', label: 'Rev main to next build#', actor: 'human', note: 'Only AFTER both stores confirm upload (rev-main-after-cut-uploads).' },
];

// The workflow whose runs carry the build/upload status the checklist reflects
// is Build Mobile Artifacts (triggered by the release-branch tag push). We link
// there — NOT release.yml, which is the #737 --ref-main trap the runbook warns
// against. A per-release `workflowRunUrl` in the doc overrides this fallback.
export const ACTIONS_URL = 'https://github.com/digimill-ie/sailing_navigator/actions/workflows/build-mobile.yml';
// The 'create draft release' ci step links to ITS OWN workflow (draft-release.yml
// stages the ASC version + Play draft), not build-mobile.yml. A per-release
// `draftRunUrl` in the doc overrides this fallback (as workflowRunUrl does for
// the build run).
export const DRAFT_RELEASE_ACTIONS_URL = 'https://github.com/digimill-ie/sailing_navigator/actions/workflows/draft-release.yml';
export const DEFAULT_ASC_URL = 'https://appstoreconnect.apple.com/apps';
export const DEFAULT_PLAY_URL = 'https://play.google.com/console';

/**
 * Marketing version — the build-number qualifier dropped. `1.3.6+180` ->
 * `1.3.6`. Release docs are keyed by this (not the full version) so a committee
 * copy edit and the push-release-notes read land on the SAME doc regardless of
 * build number (ra-ajh7 / ra-s0g3).
 */
export function marketingVersion(version) {
  return String(version || '').trim().split('+')[0].trim();
}

/**
 * Firestore doc id for a release-status doc, keyed by MARKETING version only.
 * `1.3.6+180` -> `1_3_6`, `1.3.6` -> `1_3_6`. Dropping the build number (and
 * with it the '+') eliminates the docId mismatch of ra-ajh7 — committee-write
 * and push-release-notes read now derive the identical id.
 */
export function docIdForVersion(version) {
  const mv = marketingVersion(version);
  if (!mv) return '';
  return mv.replace(/[/\\. ]/g, '_');
}

/**
 * Effective status of a step, given the automated status map (written by the
 * release workflow / mayor) and human overrides. A human "mark done" wins;
 * otherwise the automated status; otherwise pending.
 */
export function effectiveStatus(stepId, { automated = {}, manual = {} } = {}) {
  if (manual[stepId] === true) return STATUS.DONE;
  const auto = automated[stepId];
  if (typeof auto === 'string' && VALID_STATUSES.has(auto)) return auto;
  return STATUS.PENDING;
}

/** Progress across the whole runbook. */
export function computeProgress(status) {
  const done = RUNBOOK.filter(
    (s) => effectiveStatus(s.id, status) === STATUS.DONE,
  ).length;
  const total = RUNBOOK.length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

/**
 * Derive the prod cut build number from the latest ASC nightly build number.
 * Published builds are multiples of 10; the prod cut is the nightly rounded UP
 * to the next multiple of 10 (e.g. nightly 166 -> 170; 161 -> 170; 160 -> 170).
 * Rounding to the *next* decade guarantees the prod number exceeds every
 * nightly in that decade, so a versionCode is never reused. Returns null for a
 * non-positive / non-numeric input.
 */
export function deriveProdBuild(nightly) {
  const n = Number(nightly);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (Math.floor(n / 10) + 1) * 10;
}

/**
 * Resolve the deep-link for a step's action, honouring per-release overrides.
 * The 'web' preview URL is intentionally per-release and NOT hardcoded — it is
 * only shown when the release_status doc carries `webPreviewUrl` (ra-an8b.1),
 * so it tracks each web cut's preview branch rather than a stale constant.
 */
export function resolveActionHref(step, doc = {}) {
  switch (step.action) {
    case 'workflow':
      return doc.workflowRunUrl || ACTIONS_URL;
    case 'draft':
      return doc.draftRunUrl || DRAFT_RELEASE_ACTIONS_URL;
    case 'asc':
      return doc.ascUrl || DEFAULT_ASC_URL;
    case 'play':
      return doc.playUrl || DEFAULT_PLAY_URL;
    case 'web':
      return doc.webPreviewUrl || null;
    default:
      return null;
  }
}
